import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Check, AlertCircle, Landmark, Undo2 } from "lucide-react";
import { toast } from "sonner";

const CURRENT_FY = (() => {
  const now = new Date();
  const y = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
})();
const FY_OPTIONS = [-2, -1, 0, 1].map((d) => {
  const y = parseInt(CURRENT_FY.split("-")[0], 10) + d;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
}).reverse();

export default function DocumentsTrackerTab({ clientId }) {
  const { user } = useAuth();
  const canEdit = ["Admin", "Manager", "Executive"].includes(user.role);
  const canDelete = ["Admin", "Manager"].includes(user.role);
  const [fy, setFy] = useState(CURRENT_FY);
  const [banks, setBanks] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [bankDialog, setBankDialog] = useState(false);
  const [trackerDialog, setTrackerDialog] = useState(false);
  const [markDialog, setMarkDialog] = useState(null);   // { itemId, tracker, item }

  const load = async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        api.get(`/clients/${clientId}/bank-accounts`),
        api.get(`/clients/${clientId}/document-trackers`, { params: { financial_year: fy } }),
      ]);
      setBanks(b.data || []);
      setTrackers(t.data || []);
    } catch (e) {
      toast.error("Failed to load trackers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, fy]);

  const totals = useMemo(() => {
    let pending = 0, available = 0;
    for (const t of trackers) {
      for (const i of (t.items || [])) {
        if (i.status === "available") available++; else pending++;
      }
    }
    return { pending, available };
  }, [trackers]);

  const removeBank = async (b) => {
    if (!window.confirm(`Delete bank account "${b.bank_name}"? All associated trackers will also be removed.`)) return;
    await api.delete(`/clients/${clientId}/bank-accounts/${b.id}`);
    toast.success("Removed"); load();
  };
  const removeTracker = async (t) => {
    if (!window.confirm(`Delete tracker "${t.name}"?`)) return;
    await api.delete(`/clients/${clientId}/document-trackers/${t.id}`);
    toast.success("Deleted"); load();
  };
  const markAvailable = async (item, note) => {
    await api.post(`/document-items/${item.id}/mark-available`, { note });
    toast.success("Marked available"); load();
  };
  const revertItem = async (item) => {
    if (!window.confirm(`Revert this item back to pending?`)) return;
    await api.post(`/document-items/${item.id}/revert`, {});
    toast.success("Reverted"); load();
  };

  return (
    <div className="space-y-6" data-testid="documents-tracker-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Documents Tracker</h3>
          <p className="text-xs text-slate-500 mt-0.5">Track availability of recurring documents — bank statements, agreements, certificates.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="doc-tracker-fy"><SelectValue /></SelectTrigger>
            <SelectContent>{FY_OPTIONS.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Only pending</SelectItem>
              <SelectItem value="available">Only available</SelectItem>
            </SelectContent>
          </Select>
          {canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={() => setBankDialog(true)} data-testid="btn-add-bank">
                <Landmark size={12} className="mr-1.5" /> Add bank
              </Button>
              <Button size="sm" onClick={() => setTrackerDialog(true)} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="btn-add-tracker">
                <Plus size={12} className="mr-1.5" /> New tracker
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 rounded-sm bg-amber-50 border border-amber-200 text-amber-800" data-testid="doc-pending-count">
          <AlertCircle size={11} className="inline mr-1" /> {totals.pending} pending
        </span>
        <span className="px-2 py-1 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="doc-available-count">
          <Check size={11} className="inline mr-1" /> {totals.available} available
        </span>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
      ) : trackers.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-slate-300 rounded-sm">
          <p className="text-sm text-slate-500">No trackers yet for FY {fy}.</p>
          {canEdit && (
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setTrackerDialog(true)}>
              <Plus size={12} className="mr-1.5" /> Create your first tracker
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {trackers.map((t) => (
            <TrackerCard
              key={t.id}
              tracker={t}
              banks={banks}
              statusFilter={statusFilter}
              canDelete={canDelete}
              onDelete={removeTracker}
              onMark={(item) => setMarkDialog({ tracker: t, item })}
              onRevert={revertItem}
              canEdit={canEdit}
            />
          ))}
        </div>
      )}

      {/* Bank accounts panel */}
      <div>
        <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-2">Bank accounts</div>
        {banks.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No bank accounts added yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {banks.map((b) => (
              <div key={b.id} className="border border-slate-200 rounded-sm p-2.5 flex items-center justify-between text-xs" data-testid={`bank-account-${b.id}`}>
                <div>
                  <div className="font-semibold">{b.nickname || b.bank_name}</div>
                  <div className="text-slate-500">{b.bank_name}{b.account_number_last4 ? ` · ****${b.account_number_last4}` : ""}</div>
                </div>
                {canDelete && (
                  <button onClick={() => removeBank(b)} className="text-slate-400 hover:text-red-600">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AddBankDialog open={bankDialog} onOpenChange={setBankDialog} clientId={clientId} onDone={load} />
      <AddTrackerDialog open={trackerDialog} onOpenChange={setTrackerDialog} clientId={clientId}
                        banks={banks} fy={fy} onDone={load} />
      <MarkAvailableDialog markDialog={markDialog} onClose={() => setMarkDialog(null)}
                           onConfirm={async (note) => {
                             await markAvailable(markDialog.item, note);
                             setMarkDialog(null);
                           }} />
    </div>
  );
}

function TrackerCard({ tracker, banks, statusFilter, canDelete, canEdit, onDelete, onMark, onRevert }) {
  const bank = tracker.source_entity_type === "bank_account"
    ? banks.find((b) => b.id === tracker.source_entity_id) : null;
  const filtered = (tracker.items || []).filter((i) => statusFilter === "all" || i.status === statusFilter);
  const pending = (tracker.items || []).filter((i) => i.status === "pending").length;
  const total = (tracker.items || []).length;

  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid={`tracker-${tracker.id}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <div className="text-sm font-semibold text-[#0a2540]">{tracker.name}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">
            {tracker.category} · {tracker.period_type} · FY {tracker.financial_year}
            {bank && <> · {bank.nickname || bank.bank_name}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-sm font-semibold ${pending === 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
            {pending} / {total} pending
          </span>
          {canDelete && (
            <button onClick={() => onDelete(tracker)} className="text-slate-300 hover:text-red-600">
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="p-3">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-12 gap-2">
          {filtered.map((it) => (
            <div key={it.id}
                 className={`border rounded-sm p-2 text-center transition-all ${
                   it.status === "available"
                     ? "bg-emerald-50 border-emerald-200"
                     : "bg-white border-slate-200 hover:border-[#0a2540]/50"
                 }`}
                 data-testid={`doc-item-${it.id}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                {it.period_label}
              </div>
              {it.status === "available" ? (
                <div className="mt-1.5">
                  <Check size={14} className="mx-auto text-emerald-600" />
                  <div className="text-[9px] text-emerald-800 mt-1 truncate" title={it.marked_by_name}>
                    {it.marked_by_name || "—"}
                  </div>
                  {canEdit && (
                    <button onClick={() => onRevert(it)}
                            className="text-[9px] text-slate-400 hover:text-red-500 mt-1 inline-flex items-center gap-0.5"
                            data-testid={`btn-revert-${it.id}`}>
                      <Undo2 size={9} /> Revert
                    </button>
                  )}
                </div>
              ) : (
                canEdit ? (
                  <button onClick={() => onMark(it)}
                          className="text-[10px] text-[#0a2540] font-semibold mt-1.5 hover:underline"
                          data-testid={`btn-mark-${it.id}`}>
                    Mark available
                  </button>
                ) : (
                  <div className="text-[10px] text-slate-400 mt-1.5">Pending</div>
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddBankDialog({ open, onOpenChange, clientId, onDone }) {
  const [bank_name, setBankName] = useState("");
  const [account_number_last4, setLast4] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [nickname, setNickname] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!bank_name.trim()) return toast.error("Bank name required");
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/bank-accounts`,
                     { bank_name, account_number_last4, ifsc, nickname });
      toast.success("Bank added");
      setBankName(""); setLast4(""); setIfsc(""); setNickname("");
      onOpenChange(false); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-add-bank">
        <DialogHeader><DialogTitle>Add bank account</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Bank name</Label><Input value={bank_name} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. HDFC Bank" data-testid="bank-name" /></div>
          <div><Label>Nickname (optional)</Label><Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="e.g. Main current account" data-testid="bank-nickname" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Last 4 of a/c #</Label><Input value={account_number_last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="1234" data-testid="bank-last4" /></div>
            <div><Label>IFSC</Label><Input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder="HDFC0001234" data-testid="bank-ifsc" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="bank-submit">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddTrackerDialog({ open, onOpenChange, clientId, banks, fy, onDone }) {
  const [name, setName] = useState("Monthly Bank Statement");
  const [category, setCategory] = useState("Bank Statement");
  const [source, setSource] = useState("bank_account");
  const [bankId, setBankId] = useState("");
  const [periodType, setPeriodType] = useState("monthly");
  const [financial_year, setFy] = useState(fy);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFy(fy); }, [fy]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    if (source === "bank_account" && !bankId) return toast.error("Pick a bank account");
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/document-trackers`, {
        name, category,
        source_entity_type: source,
        source_entity_id: source === "bank_account" ? bankId : null,
        period_type: periodType,
        financial_year,
      });
      toast.success("Tracker created — items materialized as pending");
      onOpenChange(false); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tracker");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-tracker">
        <DialogHeader><DialogTitle>New document tracker</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Tracker name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="tracker-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="tracker-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Bank Statement", "Agreement", "Certificate", "Challan", "Proof", "Annual Report", "Other"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Period type</Label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger data-testid="tracker-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly (12 items)</SelectItem>
                  <SelectItem value="quarterly">Quarterly (4 items)</SelectItem>
                  <SelectItem value="annual">Annual (1 item)</SelectItem>
                  <SelectItem value="one_time">One-time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Financial year</Label>
              <Select value={financial_year} onValueChange={setFy}>
                <SelectTrigger data-testid="tracker-fy"><SelectValue /></SelectTrigger>
                <SelectContent>{FY_OPTIONS.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Linked source</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="tracker-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_account">Bank account</SelectItem>
                  <SelectItem value="generic">Generic (no link)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {source === "bank_account" && (
            <div><Label>Bank account</Label>
              <Select value={bankId} onValueChange={setBankId}>
                <SelectTrigger data-testid="tracker-bank"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.nickname || b.bank_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {banks.length === 0 && <p className="text-[10px] text-amber-600 mt-1">Add a bank account first.</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="tracker-submit">Create tracker</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkAvailableDialog({ markDialog, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [markDialog?.item?.id]);
  if (!markDialog) return null;
  return (
    <Dialog open={!!markDialog} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="dialog-mark-available">
        <DialogHeader>
          <DialogTitle>Mark {markDialog.item.period_label} as available</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Optional note</Label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. received via email on 5 Jul"
                    data-testid="mark-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onConfirm(note.trim() || null)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="mark-confirm">
            <Check size={13} className="mr-1.5" /> Mark available
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
